import { HttpHeaders } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'

import { environment } from '@env'
import { TicketInfo } from '@definitions/ticket'
import { ProdLocationsEnum } from '@enums/prod-locations-enum'
import { ProdOperatorsEnum } from '@enums/prod-operators-enum'

@Injectable({
	providedIn: 'root',
})
export class TicketInfoState {
	readonly ticketInfo = signal<TicketInfo | null>(null)

	/**
	 * Bumped when a payment completed outside the request/response cycle, to ask the ticket page
	 * for ticket info that already reflects it. Stripe confirms in the browser and the charge only
	 * reaches our ledger through a webhook, so the page cannot just re-read what it already has.
	 */
	readonly paymentRefreshRequests = signal(0)

	/**
	 * Bumped when the guest captured details on the unlock screen, to ask the ticket page for
	 * ticket info that reflects them.
	 *
	 * The page it unlocks reads the ticket's own guest fields to decide what to ask for next — the
	 * request-car flow asks for a phone number when the ticket has none — so without this the guest
	 * would be asked again for what they had just typed. Unlike the payment counter there is no
	 * webhook to wait for: the write committed before the card was even confirmed, so a single
	 * plain read is enough.
	 */
	readonly guestInfoRefreshRequests = signal(0)

	readonly operatorCompanyId = computed(() => this.ticketInfo()?.company?.operatorId ?? 0)
	readonly parkingLocationId = computed(() => this.ticketInfo()?.location?.id ?? 0)

	/**
	 * Address of the ticket's guest receipt, or null while there is no receipt worth opening.
	 *
	 * The ticket uuid alone does not answer whether anything was ever collected — every ticket has
	 * one from the moment it is created — and a receipt page with no charges is a confusing place
	 * to send a guest. The collected payments already in the payload answer it, so the link only
	 * appears once the ticket has taken money.
	 */
	readonly receiptUuid = computed(() => {
		const ticketInfo = this.ticketInfo()

		if (!ticketInfo?.payments?.length) return null

		return ticketInfo.ticket?.ticketUuid ?? null
	})

	/**
	 * Production rollout gate for the live ticket tracking. Drives both the Firebase credentials
	 * request and the monitoring UI, so an operator outside the rollout never reaches the endpoint
	 * and never sees the section. Drop it once the feature ships to every operator.
	 */
	readonly liveTrackingEnabled = computed(() => {
		if (!environment.production) return true

		const ticketInfo = this.ticketInfo()

		if (!ticketInfo) return false

		const operatorCompanyId = this.operatorCompanyId()
		const parkingLocationId = this.parkingLocationId()

		return (
			operatorCompanyId === ProdOperatorsEnum.CoreparkTest ||
			(operatorCompanyId === ProdOperatorsEnum.SafecoParking &&
				(parkingLocationId === ProdLocationsEnum.GaslampPlazaSuites ||
					parkingLocationId === ProdLocationsEnum.GrangerHotelGaslampQuarter))
		)
	})

	/**
	 * Whether the car-requested card shows the estimated delivery time. Safeco Parking delivers on
	 * its own schedule, so the estimate misleads its guests and is hidden for that operator.
	 */
	readonly estimatedDeliveryVisible = computed(() => {
		if (!environment.production) return true

		return this.operatorCompanyId() !== ProdOperatorsEnum.SafecoParking
	})

	/**
	 * Whether the ticket page shows the guest's check-in time. Safeco Parking asked for it to be
	 * removed from its guests' page.
	 */
	readonly checkInTimeVisible = computed(() => {
		if (!environment.production) return true

		return this.operatorCompanyId() !== ProdOperatorsEnum.SafecoParking
	})

	readonly operatorIdHeader = computed(() => {
		const operatorCompanyId = this.operatorCompanyId()

		return new HttpHeaders({
			'Operator-Id': String(operatorCompanyId),
		})
	})
	readonly headers = computed(() => {
		const operatorIdHeader = this.operatorIdHeader()
		const parkingLocationId = this.parkingLocationId()

		return operatorIdHeader.set('Location-Id', String(parkingLocationId))
	})
}
